"use client";

import { useCallback } from "react";
import type { UseFormReturn, FieldValues } from "react-hook-form";
import type { BaseTemplate } from "../../UniversalEntityPage/types";
import { extractTemplateFormValues } from "@/lib/utils/crm/template-helpers";

interface UseTemplateHandlingProps {
  form: UseFormReturn<FieldValues>;
  selectedTemplate: BaseTemplate | null;
  setSelectedTemplate: (template: BaseTemplate | null) => void;
  isSaveTemplateModalOpen: boolean;
  setIsSaveTemplateModalOpen: (open: boolean) => void;
}

/**
 * Manages template selection and form value population from templates
 */
export function useTemplateHandling({
  form,
  selectedTemplate,
  setSelectedTemplate,
  isSaveTemplateModalOpen,
  setIsSaveTemplateModalOpen,
}: UseTemplateHandlingProps) {
  const { setValue } = form;

  // Handle template selection
  const handleTemplateSelect = useCallback(
    (template: BaseTemplate | null) => {
      setSelectedTemplate(template);

      if (template) {
        const formValues = extractTemplateFormValues(template);
        Object.entries(formValues).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            setValue(key, value);
          }
        });
      }
    },
    [setValue, setSelectedTemplate]
  );

  // Open save template modal
  const handleOpenSaveTemplateModal = useCallback(() => {
    setIsSaveTemplateModalOpen(true);
  }, [setIsSaveTemplateModalOpen]);

  // Close save template modal
  const handleCloseSaveTemplateModal = useCallback(() => {
    setIsSaveTemplateModalOpen(false);
  }, [setIsSaveTemplateModalOpen]);

  return {
    selectedTemplate,
    isSaveTemplateModalOpen,
    handleTemplateSelect,
    handleOpenSaveTemplateModal,
    handleCloseSaveTemplateModal,
  };
}
